import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ArrayParam, BooleanParam, encodeQueryParams, NumberParam, StringParam} from 'serialize-query-params';
import {environment} from '../../../environments/environment';

export interface QueryParams {
  [key: string]: any;
}

export abstract class BaseService {
  protected abstract readonly endpoint: string;
  private readonly API_URL = environment.apiUrl;

  constructor(protected readonly http: HttpClient) {
  }

  protected get<T>(
    path?: string | number,
    params?: QueryParams
  ): Observable<T> {
    return this.http.get<T>(this.buildUrl(path), {
      params: this.buildHttpParams(params)
    });
  }

  protected getExternal<T>(
    baseUrl: string,
    path?: string | number,
    params?: QueryParams,
    skipEndpoint: boolean = false
  ): Observable<T> {
    const url = skipEndpoint
      ? this.buildExternalUrl(baseUrl, path)
      : this.buildExternalUrl(baseUrl, `${this.endpoint}/${path ?? ''}`);

    return this.http.get<T>(url, {
      params: this.buildHttpParams(params)
    });
  }

  protected post<T>(path: string = '', payload: any): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.post<T>(url, payload);
  }

  protected delete<T>(path: string = ''): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.delete<T>(url);
  }

  protected put<T>(path: string = '', payload: any): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.put<T>(url, payload);
  }

  protected httpDelete<T = void>(id: number | string): Observable<T> {
    return this.http.delete<T>(`${this.endpoint}/${id}`);
  }

  protected buildHttpParams(params?: QueryParams): HttpParams {
    if (!params) {
      return new HttpParams();
    }

    const encoded = encodeQueryParams(this.defineParamTypes(params), params);

    let httpParams = new HttpParams();

    Object.entries(encoded).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, value as string);
      }
    });

    return httpParams;
  }

  protected defineParamTypes(params: QueryParams): Record<string, any> {
    const types: Record<string, any> = {};

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        types[key] = ArrayParam;
      } else if (typeof value === 'number') {
        types[key] = NumberParam;
      } else if (typeof value === 'boolean') {
        types[key] = BooleanParam;
      } else {
        types[key] = StringParam;
      }
    });

    return types;
  }

  private buildUrl(path?: string | number): string {
    const api = this.API_URL.replace(/\/+$/, '');
    const endpoint = this.endpoint.replace(/^\/+|\/+$/g, '');

    if (path === undefined || path === null || path === '') {
      return `${api}/${endpoint}`;
    }

    const suffix = String(path).replace(/^\/+|\/+$/g, '');
    return `${api}/${endpoint}/${suffix}`;
  }

  private buildExternalUrl(baseUrl: string, path?: string | number): string {
    const base = baseUrl.replace(/\/+$/, '');

    if (!path && path !== 0) {
      return base;
    }

    return `${base}/${String(path).replace(/^\/+|\/+$/g, '')}`;
  }
}
